import {
  DayName,
  LiteWorkout,
  WeekName,
  WorkoutType,
  WorkoutsModel,
} from './workouts.domain';
import { Actions, Async, F1, TMap, Option } from '@client/new/helpers/types';
import {
  UseWorkoutsModelParams,
  WorkoutsModelDispatcher,
  useWorkoutsModel,
} from './workouts.model';
import { useEffect, useState } from 'react';
import { PATHS } from '@client/routes';
import { CollectionProgram } from '../../../../components/collections/collections.types';
import { keys, pickObject } from '../../../helpers/general';
import { BasicMenuProps } from '@client/new/components/BasicMenu/BasicMenu';
import { makeAsync } from '../../../helpers/async';
import { toStandardTableProps } from './workouts.viewModel.tables';
import { SmartTableProps } from '../../../components/SmartTable/SmartTable';
import { DragTableProps } from '../../../components/DragTable/DragTable';
import { workoutTabToType, workoutTypeToTab } from '../domain';
import { extendDispatch } from '@client/new/helpers/types-utils';
import { useTabHash } from '@client/new/hooks/useTabHash';

export type WorkoutNotification = {
  type: 'duplicate';
  url: string;
  success: boolean;
};

export type WorkoutsViewModel = Pick<
  WorkoutsModel,
  'isProgramEditable' | 'programName'
> & {
  listOptions: Array<Option<WorkoutType>>;
  selectedList: WorkoutType;
  dispatch: VMDispatcher;

  mode: ViewMode;
  menuProps: BasicMenuProps;
  disableMenu: boolean;

  searchText: string;
  setSearchText: F1<string>;

  hasUnassignedWorkouts: boolean;
  notifications: Array<WorkoutNotification>;
  hideNotification: F1<WorkoutNotification['type']>;
  openFlowModal: boolean;
  linkModal: {
    isOpen: boolean;
    url: Async<{ url: string }>;
  };
};

export type WorkoutsVMActionsMap = {
  selectList: WorkoutType;
  reorder: Array<number>;
  setWeek: Pick<LiteWorkout, 'workoutId'> & {
    newWeek: WeekName;
    day?: DayName;
  };
  setDay: Pick<LiteWorkout, 'workoutId'> & {
    newDay: DayName;
    week?: WeekName;
  };
  remove: Pick<LiteWorkout, 'workoutId'>;
  archive: Pick<LiteWorkout, 'workoutId'>;
  publish: Pick<LiteWorkout, 'workoutId'>;
  duplicate: LiteWorkout;
  openLinkModal: number;
  closeLinkModal: never;
  toggleInfoModal: never;
};

export type WorkoutsVMActions = Actions<WorkoutsVMActionsMap>;

export type WorkoutsViewState = Pick<
  WorkoutsViewModel,
  'notifications' | 'openFlowModal'
> & {
  searchText: string;
  awaitingActionIds: TMap<number, WorkoutNotification | undefined>;
  linkModalOpen: boolean;
  // workouts: WorkoutsModel['workouts'] | null;
};

export const listTitles: TMap<WorkoutType, string> = {
  scheduled: 'Scheduled',
  archived: 'Archived',
  draft: 'Drafts',
  published: 'Published',
};

const toListOptions = (): WorkoutsViewModel['listOptions'] => {
  const types: WorkoutType[] = ['draft', 'scheduled', 'published', 'archived'];
  return types.map((value) => ({ value, label: listTitles[value] }));
};

export type VMDispatcher = F1<WorkoutsVMActions, null>;
const getVMDispatcher =
  (
    model: WorkoutsModel & { dispatch: WorkoutsModelDispatcher },
    programId: number,
    setViewState: F1<
      | Partial<WorkoutsViewState>
      | F1<WorkoutsViewState, Partial<WorkoutsViewState>>,
      null
    >,
    selectTab: F1<WorkoutType>,
  ): VMDispatcher =>
  (action) => {
    switch (action.type) {
      case 'selectList':
        selectTab(action.payload);
        return null;
      case 'publish':
      case 'archive':
      case 'remove':
        model.dispatch(action);
        return null;
      case 'openLinkModal':
        model.dispatch({
          type: 'getDeepLink',
          payload: action.payload,
        });
        setViewState((prev) => ({
          ...prev,
          linkModalOpen: true,
        }));
        return null;
      case 'closeLinkModal':
        setViewState((prev) => ({
          ...prev,
          linkModalOpen: false,
        }));
        return null;
      case 'duplicate': {
        const aid = model.dispatch(action);
        setViewState((prev) => ({
          awaitingActionIds: {
            ...prev.awaitingActionIds,
            [aid]: {
              type: 'duplicate',
              url: PATHS.PROGRAM_EDIT(
                programId,
                model.influencerId,
                'workouts-draft',
              ),
              success: true,
            },
          },
        }));

        return null;
      }
      case 'setDay':
        model.dispatch({
          type: 'rename',
          payload: {
            workoutId: action.payload.workoutId,
            day: action.payload.newDay,
            week: action.payload.week,
          },
        });
        return null;
      case 'setWeek':
        model.dispatch({
          type: 'rename',
          payload: {
            workoutId: action.payload.workoutId,
            week: action.payload.newWeek,
            day: action.payload.day,
          },
        });
        return null;
      case 'reorder':
        model.dispatch(action);
        return null;
      case 'toggleInfoModal':
        setViewState((prev) => ({
          openFlowModal: !prev.openFlowModal,
        }));
        return null;
    }
  };

export type AllEmptyValue = {
  createWorkoutURL: string;
  createClassURL: string;
  educationalModal: {
    isOpen: boolean;
    onClose: () => void;
    source: 'workout';
    influencerId: number;
    programId: number;
  };
};

const toBasicMenuProps = (
  p: Pick<WorkoutsViewModel, 'selectedList'> & {
    programId: number;
    influencerId: number;
  },
): BasicMenuProps => ({
  title: 'Create',
  items: [
    {
      label: 'Workout',
      url: PATHS.WORKOUT_EDIT({
        workoutId: 0,
        fromWorkoutTab: workoutTypeToTab[p.selectedList],
        ...p,
      }),
    },
    {
      label: 'Class',
      url: PATHS.CLASS_EDIT({
        workoutId: 0,
        fromWorkoutTab: workoutTypeToTab[p.selectedList],
        ...p,
      }),
    },
  ],
});

const toAllEmptyValue = (
  p: Pick<WorkoutsViewModel, 'selectedList' | 'dispatch' | 'openFlowModal'> & {
    programId: number;
    influencerId: number;
  },
): AllEmptyValue => ({
  createWorkoutURL: PATHS.WORKOUT_EDIT({
    workoutId: 0,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
    ...p,
  }),
  createClassURL: PATHS.CLASS_EDIT({
    workoutId: 0,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
    ...p,
  }),
  educationalModal: {
    isOpen: p.openFlowModal,
    onClose: () => p.dispatch({ type: 'toggleInfoModal' }),
    source: 'workout',
    influencerId: p.influencerId,
    programId: p.programId,
  },
});

export type ViewMode =
  | { type: 'all-empty'; value: AllEmptyValue }
  | { type: 'selected-empty' }
  | { type: 'loading' }
  | { type: 'standard'; value: SmartTableProps<number> };

export type ToSmartTablePropsParams = {
  dispatch: VMDispatcher;
} & Pick<
  WorkoutsModel,
  | 'isProgramEditable'
  | 'workoutsStructure'
  | 'workoutsStateMap'
  | 'influencerId'
  | 'readonly'
  | 'isWorkoutArchiveEnabled'
> &
  Pick<WorkoutsViewState, 'searchText'> & {
    selectedList: WorkoutType;
    workouts: Array<LiteWorkout>;
  };

export const toSmartTableProps = (
  p: ToSmartTablePropsParams,
): SmartTableProps<number> => {
  const { dispatch } = extendDispatch<WorkoutsVMActionsMap>(p.dispatch);
  const filteredCurrentList = p.workouts.filter((w) =>
    w.orgTitle.toLocaleLowerCase().includes(p.searchText.toLocaleLowerCase()),
  );

  const tableProps = toStandardTableProps({
    ...p,
    workouts: filteredCurrentList,
  });

  const isDraggable =
    !p.readonly &&
    p.selectedList === 'published' &&
    p.workoutsStructure === 'series' &&
    filteredCurrentList.length === p.workouts.length;

  const onChange: DragTableProps<number>['onChange'] = (rows) => {
    const workoutIds = rows.map((r) => r.id);
    dispatch('reorder', workoutIds);
  };

  return isDraggable
    ? { type: 'draggable', value: { ...tableProps, onChange } }
    : { type: 'standard', value: tableProps };
};

export const toViewMode = (
  model: WorkoutsModel,
  viewState: WorkoutsViewState,
  collection: CollectionProgram,
  dispatch: VMDispatcher,
  selectedList: WorkoutType,
): ViewMode => {
  if (
    selectedList === 'published' &&
    Object.values(model.workouts).every((l) => !l.length)
  )
    return {
      type: 'all-empty',
      value: toAllEmptyValue({
        influencerId: model.influencerId,
        programId: collection.id,
        selectedList,
        dispatch,
        openFlowModal: viewState.openFlowModal,
      }),
    };

  if (model.workouts[selectedList].length === 0 && model.isSelectedListLoading)
    return { type: 'loading' };

  if (model.workouts[selectedList].length === 0)
    return { type: 'selected-empty' };

  const value = toSmartTableProps({
    dispatch,
    ...pickObject(model, [
      'isProgramEditable',
      'workoutsStructure',
      'workoutsStateMap',
      'influencerId',
      'readonly',
      'isWorkoutArchiveEnabled',
    ]),
    searchText: viewState.searchText,
    selectedList,
    workouts: model.workouts[selectedList],
  });

  return { type: 'standard', value };
};

const getUnassignedWorkouts = (
  {
    workouts,
    workoutsStructure,
  }: Pick<WorkoutsModel, 'workoutsStructure' | 'workouts'>,
  selectedList: WorkoutType,
) =>
  workoutsStructure === 'week/day' &&
  selectedList === 'published' &&
  workouts.published.some((w) => w.week === undefined || w.day === undefined);

export const useWorkoutsViewModel = (
  p: UseWorkoutsModelParams,
): Async<WorkoutsViewModel> => {
  const { tabHash, onTabChange } = useTabHash(
    keys(workoutTabToType),
    'workouts-published',
  );
  const selectedList = workoutTabToType[tabHash];

  const [viewState, setViewState] = useState<WorkoutsViewState>({
    searchText: '',
    notifications: [],
    awaitingActionIds: {},
    openFlowModal: false,
    linkModalOpen: false,
  });
  const asyncModel = useWorkoutsModel(p, selectedList);

  const setSearchText = (searchText: string) => {
    setViewState((prev) => ({ ...prev, searchText }));
  };

  useEffect(() => {
    if (asyncModel.type !== 'fetched') return;
    const notifications: WorkoutNotification[] = [];
    const awaitingActionIds = { ...viewState.awaitingActionIds };
    const { actionResults } = asyncModel.value;
    keys(actionResults)
      .filter(
        (aid) =>
          actionResults[aid].type !== 'fetching' &&
          viewState.awaitingActionIds[aid] !== undefined,
      )
      .forEach((aid) => {
        const notification = awaitingActionIds[aid];
        if (!notification) return;
        notifications.push({
          ...notification,
          success: actionResults[aid].type === 'fetched',
        });
        awaitingActionIds[aid] = undefined;
      });
    if (notifications.length)
      setViewState((prev) => ({ ...prev, awaitingActionIds, notifications }));
  }, [asyncModel, viewState.awaitingActionIds]);

  if (asyncModel.type !== 'fetched') return asyncModel;
  const model = asyncModel.value;

  const dispatch = getVMDispatcher(
    model,
    p.collection.id,
    (state) => {
      if (typeof state === 'function')
        setViewState((p) => ({ ...p, ...state(p) }));
      else setViewState((p) => ({ ...p, ...state }));
      return null;
    },
    (type) => onTabChange(null, workoutTypeToTab[type]),
  );

  return makeAsync.fetched({
    listOptions: toListOptions(),
    setSearchText,
    linkModal: {
      isOpen: viewState.linkModalOpen,
      url: model.workoutDeepLink,
    },
    dispatch,
    isProgramEditable: model.isProgramEditable,
    programName: model.programName,
    hasUnassignedWorkouts: getUnassignedWorkouts(model, selectedList),
    disableMenu: p.readonly,
    menuProps: toBasicMenuProps({
      influencerId: model.influencerId,
      programId: p.collection.id,
      selectedList,
    }),
    mode: toViewMode(model, viewState, p.collection, dispatch, selectedList),
    hideNotification: (type) =>
      setViewState((p) => ({
        ...p,
        notifications: p.notifications.filter((n) => n.type !== type),
      })),
    selectedList,
    ...viewState,
  } as WorkoutsViewModel);
};
